import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, X, Calendar } from "lucide-react";

const UploadModal = ({ open, onOpenChange, onSuccess, token }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [referenceDate, setReferenceDate] = useState(new Date().toISOString().split('T')[0]);
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.match(/\.(xlsx|xls)$/i)) {
        setFile(droppedFile);
      } else {
        toast.error("Formato non supportato. Usa file .xlsx o .xls");
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!referenceDate) {
      toast.error("Inserisci la data di riferimento per il calcolo del rischio");
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("reference_date", referenceDate);
    
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      toast.success(`Caricati ${response.data.count} preventivi con successo!`);
      onSuccess(response.data.session_id);
      setFile(null);
      setReferenceDate(new Date().toISOString().split('T')[0]);
    } catch (error) {
      const message = error.response?.data?.detail || "Errore durante il caricamento";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setReferenceDate(new Date().toISOString().split('T')[0]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="upload-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase tracking-tight">
            Carica File Excel
          </DialogTitle>
          <DialogDescription>
            Importa i tuoi preventivi da un file Excel (.xlsx o .xls)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Reference Date Input */}
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-5 w-5 text-orange-600" />
              <Label htmlFor="reference-date" className="text-sm font-semibold text-orange-800">
                Data di Riferimento per Calcolo Rischio
              </Label>
            </div>
            <p className="text-xs text-orange-700 mb-3">
              Il rischio verrà calcolato in base alla differenza tra questa data e la data di creazione del preventivo
            </p>
            <Input
              id="reference-date"
              type="date"
              value={referenceDate}
              onChange={(e) => setReferenceDate(e.target.value)}
              className="bg-white border-orange-300 focus:border-orange-500"
              data-testid="reference-date-input"
            />
            <div className="mt-2 text-xs text-slate-600">
              <strong>Livelli di rischio:</strong> Verde (0-3 gg) | Medio (4-7 gg) | Forte (8-14 gg) | Molto Forte (&gt;14 gg)
            </div>
          </div>

          {/* Required columns info */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm font-medium text-slate-700 mb-2">Colonne riconosciute automaticamente:</p>
            <div className="flex flex-wrap gap-2">
              {["Sede", "Responsable/TC", "CódigoComessa", "TipoSujeto", "Sujeto/Condominio", "Dirección", "FechaCreaciónPráctica", "Presupuestado€"].map(col => (
                <span key={col} className="text-xs bg-white px-2 py-1 rounded border border-slate-200 text-slate-600">
                  {col}
                </span>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">Il sistema riconosce automaticamente colonne in italiano, spagnolo e inglese</p>
          </div>

          {/* Drop Zone */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? "border-orange-500 bg-orange-50" 
                : file 
                  ? "border-green-500 bg-green-50" 
                  : "border-slate-300 hover:border-slate-400"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            data-testid="upload-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
              data-testid="file-input"
            />
            
            {file ? (
              <div className="space-y-3">
                <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
                <div>
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setFile(null)}
                  className="mt-2"
                >
                  <X className="h-4 w-4 mr-2" />
                  Rimuovi
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <FileSpreadsheet className="h-12 w-12 mx-auto text-slate-400" />
                <div>
                  <p className="font-medium text-slate-700">
                    Trascina qui il tuo file Excel
                  </p>
                  <p className="text-sm text-slate-500">
                    oppure{" "}
                    <button
                      onClick={() => inputRef.current?.click()}
                      className="text-orange-500 hover:text-orange-600 font-medium"
                    >
                      sfoglia
                    </button>
                    {" "}per selezionarlo
                  </p>
                </div>
                <p className="text-xs text-slate-400">
                  Formati supportati: .xlsx, .xls
                </p>
              </div>
            )}
          </div>

          {/* Upload Button */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose} disabled={uploading}>
              Annulla
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={!file || uploading}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="upload-submit-btn"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Caricamento...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Carica
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UploadModal;
